/**
 * Phase 8 T-25 §29.B — Paraphrase turn (Stage 1).
 *
 * Renders the agent's paraphrase of the user's JD with bolded keywords
 * via `renderBoldKeywords()` → `<mark class="ggj-keyword">`. The bolding
 * is the visible proof of comprehension.
 *
 * Anatomy (per §29.B):
 *   - Emerald agent icon (Sparkles) + `GGJ Reasoning v2` model label
 *     + 11 px meta `Thought for {X}s`.
 *   - 2-3 short paraphrase paragraphs.
 *   - A final short paragraph that is the SINGLE clarifying question
 *     — unless `suppressQuestion` is true (round 3 hard cap, §29.B).
 */

import { AgentMark } from '@shared/components/AgentMark';
import { renderBoldKeywords } from '@shared/utils/renderBoldKeywords';

interface ParaphraseTurnProps {
  /**
   * The paraphrase string with `**keyword**` markdown spans. The last
   * paragraph (separated by a blank line) is treated as the clarifying
   * question and rendered with slightly different emphasis.
   */
  paraphrase: string;
  /** Display time the agent "thought" — shown as `Thought for Xs`. */
  thoughtMs: number;
  /**
   * When true, the last paragraph is rendered as a regular paragraph
   * (not a question). Used after round 2 to stop asking (§29.B cap).
   */
  suppressQuestion?: boolean;
}

export function ParaphraseTurn({
  paraphrase,
  thoughtMs,
  suppressQuestion = false,
}: ParaphraseTurnProps) {
  const trimmed = paraphrase.trim();
  if (!trimmed) return null;

  // Split on blank lines. The last paragraph is the clarifying question
  // (unless suppressed). All other paragraphs are body copy.
  const paragraphs = trimmed.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const bodyParagraphs = suppressQuestion ? paragraphs : paragraphs.slice(0, -1);
  const questionParagraph = suppressQuestion ? null : paragraphs.at(-1) ?? null;

  const seconds = Math.max(0.1, thoughtMs / 1000);

  return (
    <div className="flex items-start gap-2.5" data-ggj-paraphrase-turn>
      {/* Chat avatar — AgentMark pinned to the left edge of the turn
          so the thread reads as agent⇄user speaker rows. */}
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full shadow-sm"
        style={{
          background: 'var(--vscode-bg-elevated)',
          border: '1px solid color-mix(in srgb, var(--ggj-accent,#10B981) 25%, var(--vscode-border-primary))',
        }}
        aria-hidden
      >
        <AgentMark className="h-4 w-4" ariaLabel={null} />
      </div>

      <div className="min-w-0 flex-1">
        {/* Speaker / model line — sits above the bubble like Slack/Linear */}
        <div className="mb-1 flex items-center gap-2">
          <span
            className="text-[12px] font-semibold"
            style={{ color: 'var(--vscode-text-primary)' }}
          >
            GGJ Reasoning v2
          </span>
          <span
            className="text-[11px]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            Thought for {seconds.toFixed(1)}s
          </span>
        </div>

        {/* Bubble body. Rounded left corner is small (4px) to create the
            classic "chat tail" effect where the bubble points to the
            avatar — matches the Wrangle reference and the user-side
            right-aligned bubble (`UserRefinementBubble`). */}
        <div
          className="max-w-[78%] rounded-[14px] rounded-tl-[4px] border px-4 py-3 shadow-sm"
          style={{
            background: 'color-mix(in srgb, var(--vscode-bg-elevated) 92%, transparent)',
            borderColor: 'color-mix(in srgb, var(--vscode-border-primary) 80%, transparent)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        >
          <div className="space-y-2.5">
            {bodyParagraphs.map((p, i) => (
              <p
                key={`body-${i}`}
                className="text-[13px] leading-[1.6]"
                style={{ color: 'var(--vscode-text-primary)' }}
              >
                {renderBoldKeywords(p)}
              </p>
            ))}

            {/* Clarifying question — callout with emerald accent bar */}
            {questionParagraph ? (
              <p
                className="mt-2 border-l-2 pl-3 py-1 text-[13px] leading-[1.6]"
                style={{
                  borderLeftColor: 'var(--ggj-accent,#10B981)',
                  color: 'var(--vscode-text-primary)',
                }}
                role="note"
              >
                {renderBoldKeywords(questionParagraph)}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
